/**
 * useLoginSessionCheck Hook
 *
 * Prevents conflicting logins across tabs by:
 * 1. Checking for existing sessions before login submission (Pre-submit check)
 * 2. Listening for new sessions created in other tabs (Real-time sync)
 *
 * Usage in login pages (SigninPage, AdminLoginPage, NomineeLoginPage):
 * const { checkSessionBeforeLogin, showRoleSwitchModal, currentRole, handleConfirmSwitch, handleCancelSwitch } =
 *   useLoginSessionCheck(targetRole, navigate);
 */

import { useState, useEffect, useCallback } from 'react';
import { detectActiveSession, isRoleSwitch, clearRoleSession, getRoleDashboardRoute, getRoleDisplayName, SESSION_TYPES } from '../utils/sessionManager';
import { initTabSync, stopTabSync, broadcastNewSession } from '../utils/tabSync';
import logger from '../utils/logger';

const TAB_SYNC_ENABLED = import.meta.env.VITE_ENABLE_TAB_SYNC !== 'false';

export const useLoginSessionCheck = (targetRole, navigate) => {
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);

  // Pre-submit session check
  const checkSessionBeforeLogin = useCallback(() => {
    const activeSession = detectActiveSession();

    logger.log(`[LoginSessionCheck] Pre-submit check: Active=${activeSession}, Target=${targetRole}`);

    // No active session - proceed with login
    if (activeSession === SESSION_TYPES.NONE) {
      return true; // Allow login
    }

    // Same role - shouldn't happen if SessionGuard works, but handle it
    if (activeSession === targetRole) {
      logger.warn(`[LoginSessionCheck] Already logged in as ${targetRole}, redirecting to dashboard`);
      const dashboardRoute = getRoleDashboardRoute(targetRole);
      navigate(dashboardRoute, { replace: true });
      return false; // Block login
    }

    // Different role - show confirmation modal
    if (isRoleSwitch(targetRole)) {
      logger.log(`[LoginSessionCheck] Role conflict detected: ${activeSession} → ${targetRole}`);
      setCurrentRole(activeSession);
      setShowRoleSwitchModal(true);
      return false; // Block login until confirmed
    }

    return true; // Default: allow
  }, [targetRole, navigate]);

  // Broadcast new session after successful login
  const broadcastLogin = useCallback((roleType) => {
    if (TAB_SYNC_ENABLED) {
      // Broadcast on all channels to notify all tabs
      broadcastNewSession(roleType, 'digitalvault-session-user');
      broadcastNewSession(roleType, 'digitalvault-session-admin');
      broadcastNewSession(roleType, 'digitalvault-session-nominee');
      logger.log(`[LoginSessionCheck] Broadcasted new ${roleType} session to all channels`);
    }
  }, []);

  // Real-time session detection via BroadcastChannel
  useEffect(() => {
    if (!TAB_SYNC_ENABLED) return;

    // Listen on all channels for new sessions
    const channels = [
      'digitalvault-session-user',
      'digitalvault-session-admin',
      'digitalvault-session-nominee'
    ];

    const callbacks = {
      onNewSession: (data) => {
        logger.log(`[LoginSessionCheck] New session detected: ${data.roleType}`);

        // Check if this conflicts with our target role
        const activeSession = detectActiveSession();

        if (activeSession !== SESSION_TYPES.NONE && isRoleSwitch(targetRole)) {
          logger.log(`[LoginSessionCheck] Real-time conflict: ${activeSession} vs ${targetRole}`);
          setCurrentRole(activeSession);
          setShowRoleSwitchModal(true);
        }
      }
    };

    // Initialize all channels
    channels.forEach(channel => {
      initTabSync(callbacks, channel);
    });

    // Cleanup
    return () => {
      channels.forEach(channel => {
        stopTabSync(channel);
      });
    };
  }, [targetRole]);

  // Handle user confirming the role switch
  const handleConfirmSwitch = useCallback(() => {
    logger.log(`[LoginSessionCheck] User confirmed switch from ${currentRole} to ${targetRole}`);

    // Clear the current session
    clearRoleSession(currentRole);

    // Close modal and allow login to proceed
    setShowRoleSwitchModal(false);
    setCurrentRole(null);

    // Return true to indicate login can proceed
    return true;
  }, [currentRole, targetRole]);

  // Handle user cancelling the role switch
  const handleCancelSwitch = useCallback(() => {
    logger.log(`[LoginSessionCheck] User cancelled switch, staying as ${currentRole}`);

    // Redirect to current role's dashboard
    const dashboardRoute = getRoleDashboardRoute(currentRole);
    navigate(dashboardRoute, { replace: true });

    // Close modal
    setShowRoleSwitchModal(false);
    setCurrentRole(null);
  }, [currentRole, navigate]);

  // Get display names for modal
  const currentRoleDisplay = currentRole ? getRoleDisplayName(currentRole) : '';
  const targetRoleDisplay = getRoleDisplayName(targetRole);

  return {
    checkSessionBeforeLogin,
    broadcastLogin,
    showRoleSwitchModal,
    currentRole: currentRoleDisplay,
    targetRole: targetRoleDisplay,
    handleConfirmSwitch,
    handleCancelSwitch
  };
};

export default useLoginSessionCheck;
